import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["error", "label", "FormLabel", "validationMessage", "help"];
/* eslint-disable @typescript-eslint/no-floating-promises */

import { useState, useCallback, memo, useMemo } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import KnowledgeBaseButton from 'ui-addon-i18n/components/KnowledgeBaseButton';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { fetchIsCampaignAtLimit } from 'campaigns-lib/data/CampaignDao';
import { MemoizedCampaignSelectComponent as CampaignSelect } from 'campaigns-lib/components/CampaignSelect';
import { SUPPORTED_ASSET_OBJECTS_TYPES } from 'campaigns-lib/constants/campaignSelect';
import useGetCampaignSelectChangeHandler from 'campaigns-lib/hooks/useGetCampaignSelectChangeHandler';
import { formatAssetLabel } from 'campaigns-lib/utils/campaignSelect';
import { View } from 'foundations-components/transitional/components/View';
import { Text } from 'foundations-components/components/Text';
import MarketingStudioOptinWrapper, { useStudioOptinContext } from '../studio/MarketingStudioOptinWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function CampaignSelectFormControlComponent({
  applicationId,
  assetObjectType,
  enableTracking,
  formControlProps = {},
  hasCampaignsReadAccess,
  hasCampaignsWriteAccess,
  multi,
  value,
  required,
  onChange,
  showMissingAccessError = false,
  editorSelect = false,
  assetLabel,
  formHelpAsParagraph = false,
  disabledTooltip,
  disabledTooltipPlacement,
  withCampaignAutoSelectPopover,
  disabledOptionsByName,
  'data-test-id': dataTestId,
  disabled,
  autoLoad,
  error,
  inputRef,
  objectBuilderUiProps,
  onLearnMoreClick,
  onBlur: onBlurProp,
  helpAsTooltip = false,
  numberOfObjectsToAssociate,
  onOptionsLoaded
}) {
  const {
    hasAccessToMarketingStudio
  } = useStudioOptinContext();
  const [notFoundError, setNotFoundError] = useState();
  const [limitError, setLimitError] = useState();
  const {
      error: formControlError,
      label: formControlLabel,
      FormLabel,
      validationMessage: formControlValidationMessage,
      help: formControlHelp
    } = formControlProps,
    restFormControlProps = _objectWithoutPropertiesLoose(formControlProps, _excluded);
  const handleCurrentValueNotFound = useCallback(() => setNotFoundError(true), []);
  const handleChange = useGetCampaignSelectChangeHandler({
    multi,
    onChange
  });
  const {
    labelTextAs,
    labelTextColor
  } = useMemo(() => {
    if (formHelpAsParagraph) {
      return {
        labelTextAs: 'p',
        labelTextColor: 'text-core-default'
      };
    }
    if (helpAsTooltip) {
      return {
        labelTextAs: undefined,
        labelTextColor: undefined
      };
    }
    return {
      labelTextAs: 'span',
      labelTextColor: 'text-core-subtle'
    };
  }, [formHelpAsParagraph, helpAsTooltip]);
  const handleChangeWithLimitCheck = useCallback((...data) => {
    setNotFoundError(false);
    setLimitError(false);
    const [, optionOrOptions] = data;
    handleChange(optionOrOptions);
    if (assetObjectType) {
      const campaignOption = Array.isArray(optionOrOptions) ?
      // the newest campaign added is the last one
      optionOrOptions[optionOrOptions.length - 1] : optionOrOptions;
      if (campaignOption && campaignOption.guid) {
        fetchIsCampaignAtLimit({
          assetObjectType,
          campaignGuid: campaignOption.guid
        }).then(isAtLimit => {
          setLimitError(isAtLimit);
          if (isAtLimit) {
            if (multi && Array.isArray(optionOrOptions)) {
              handleChange(
              // re-triggering the change without that last campaign if multi select
              optionOrOptions.slice(0, optionOrOptions.length));
            } else {
              // unselecting the campaign if not multi select
              handleChange([]);
            }
          }
        }).catch();
      }
    }
  }, [assetObjectType, multi, handleChange]);
  const getValidationMessage = useCallback(() => {
    if (notFoundError && showMissingAccessError) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "campaignSelect.validationErrors.campaignNotFoundWithNoAccess"
      });
    } else if (notFoundError) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "campaignSelect.validationErrors.campaignNotFound"
      });
    } /*else if (!hasCampaignsWriteAccess) {
      return (
        <FormattedMessage message="campaignSelect.validationErrors.noWriteAccess" />
      );
      }*/else if (limitError && assetObjectType) {
      if (SUPPORTED_ASSET_OBJECTS_TYPES.indexOf(assetObjectType) > -1) {
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: `campaignSelect.validationErrors.limitError.${assetObjectType}`
        });
      } else {
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "campaignSelect.validationErrors.limitError.generic"
        });
      }
    }
    return null;
  }, [assetObjectType,
  // hasCampaignsWriteAccess,
  limitError, notFoundError, showMissingAccessError]);
  if (!hasCampaignsReadAccess) {
    return null;
  }
  if (editorSelect) {
    const help = formControlHelp || !hasAccessToMarketingStudio && /*#__PURE__*/_jsx(Text, {
      as: labelTextAs,
      whiteSpace: "normal",
      color: labelTextColor,
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "campaignSelect.formEditorControlDescription_jsx",
        options: Object.assign({
          url: 'https://knowledge.hubspot.com/campaigns/understand-campaigns',
          use: 'link',
          useZorse: true,
          padding: '0',
          assetLabel: formatAssetLabel(assetObjectType, assetLabel),
          assetLabelCapitalized: assetLabel || I18n.text('campaignSelect.defaultAssetLabel')
        }, {
          marginBlockStart: formHelpAsParagraph ? '-3px' : '-2px'
        }, {
          onClick: onLearnMoreClick
        }),
        elements: {
          Button: KnowledgeBaseButton
        }
      })
    });
    return /*#__PURE__*/_jsx(FormControl, Object.assign({
      onBlur: onBlurProp,
      error: notFoundError || limitError || formControlError /*||
                                                             !hasCampaignsWriteAccess*/,
      label: hasAccessToMarketingStudio ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: multi ? 'campaignSelect.multiMarketingCampaign' : 'campaignSelect.marketingCampaign'
      }) : formControlLabel || /*#__PURE__*/_jsx(FormattedMessage, {
        message: "campaignSelect.formEditorControlLabel"
      }),
      FormLabel: FormLabel || undefined,
      help: !formHelpAsParagraph && !helpAsTooltip && help,
      tooltipProps: {
        'data-test-id': 'campaign-select-form-control-tooltip'
      },
      tooltip: helpAsTooltip ? help : undefined,
      validationMessage: getValidationMessage() || formControlValidationMessage,
      "data-test-id": "campaign-select-form-control"
    }, restFormControlProps, {
      children: /*#__PURE__*/_jsxs("span", {
        "data-test-id": "campaign-select-input-wrapper",
        children: [!helpAsTooltip && formHelpAsParagraph && help, /*#__PURE__*/_jsx(View, {
          marginBlockStart: "200",
          children: /*#__PURE__*/_jsx(CampaignSelect, {
            assetObjectType: assetObjectType,
            onChange: handleChangeWithLimitCheck,
            onCurrentValueNotFound: handleCurrentValueNotFound,
            value: value,
            canCreateCampaigns: hasCampaignsWriteAccess,
            multi: multi,
            enableTracking: enableTracking !== undefined ? enableTracking : true,
            autoLoad: autoLoad || true,
            withEmptyState: true,
            disabledTooltip: disabledTooltip,
            disabledTooltipPlacement: disabledTooltipPlacement,
            withCampaignAutoSelectPopover: withCampaignAutoSelectPopover,
            disabledOptionsByName: disabledOptionsByName,
            applicationId: applicationId,
            uiSelectProps: {
              error: error || notFoundError || limitError /*|| !hasCampaignsWriteAccess*/,
              placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
                message: multi ? 'campaignSelect.multiPlaceholder' : 'campaignSelect.selectCampaign'
              }),
              'data-test-id': dataTestId,
              disabled,
              inputRef,
              anchorType: multi ? 'button' : 'auto'
            },
            objectBuilderUiProps: objectBuilderUiProps,
            numberOfObjectsToAssociate: numberOfObjectsToAssociate,
            onOptionsLoaded: onOptionsLoaded
          })
        })]
      })
    }));
  }
  const formControlTooltip = multi ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
    message: 'campaignSelect.oneToManyTooltip_jsx',
    options: {
      url: 'https://knowledge.hubspot.com/campaigns/understand-campaigns',
      use: 'link',
      useZorse: true,
      padding: '0'
    },
    elements: {
      Button: KnowledgeBaseButton
    }
  }) : I18n.text('campaignSelect.formControlTooltip');
  return /*#__PURE__*/_jsx(FormControl, {
    onBlur: onBlurProp,
    error: notFoundError || limitError || formControlError,
    label: formControlLabel || /*#__PURE__*/_jsx(FormattedMessage, {
      message: "campaignSelect.formControlLabel",
      options: {
        count: 1
      }
    }),
    FormLabel: FormLabel || undefined,
    tooltip: formControlTooltip,
    validationMessage: getValidationMessage() || formControlValidationMessage,
    required: required,
    children: /*#__PURE__*/_jsx(CampaignSelect, {
      assetObjectType: assetObjectType,
      onChange: handleChangeWithLimitCheck,
      onCurrentValueNotFound: handleCurrentValueNotFound,
      value: value,
      canCreateCampaigns: hasCampaignsWriteAccess,
      multi: multi,
      enableTracking: enableTracking !== undefined ? enableTracking : true,
      autoLoad: autoLoad || true,
      disabledTooltip: disabledTooltip,
      disabledTooltipPlacement: disabledTooltipPlacement,
      withCampaignAutoSelectPopover: withCampaignAutoSelectPopover,
      disabledOptionsByName: disabledOptionsByName,
      applicationId: applicationId,
      uiSelectProps: {
        error: error || notFoundError || limitError,
        placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
          message: multi ? 'campaignSelect.multiPlaceholder' : 'campaignSelect.selectCampaign'
        }),
        'data-test-id': dataTestId,
        disabled,
        inputRef,
        anchorType: multi ? 'button' : 'auto'
      },
      numberOfObjectsToAssociate: numberOfObjectsToAssociate,
      onOptionsLoaded: onOptionsLoaded
    })
  });
}
const MemoizedCampaignSelectFormControlComponent = /*#__PURE__*/memo(CampaignSelectFormControlComponent);
const CampaignSelectFormControlWithOptinWrapper = props => {
  return /*#__PURE__*/_jsx(MarketingStudioOptinWrapper, {
    shouldRenderOptin: false,
    children: /*#__PURE__*/_jsx(MemoizedCampaignSelectFormControlComponent, Object.assign({}, props))
  });
};
export default CampaignSelectFormControlWithOptinWrapper;